import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

import Step1WebsiteAnalysis from '../components/onboarding/Step1WebsiteAnalysis';
// Step2ChannelSelect intentionally not imported — platform selection was removed from onboarding.
import Step3AccountCreation from '../components/onboarding/Step3AccountCreation';
import StepAssetUpload      from '../components/onboarding/StepAssetUpload';
import StepBrandDNA         from '../components/onboarding/StepBrandDNA';
import StepPersonFinalize   from '../components/onboarding/StepPersonFinalize';
import StepIndicator        from '../components/onboarding/StepIndicator';
import LangToggle           from '../components/onboarding/LangToggle';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

// Platform-selection is skipped → 4 visible steps for new users, 3 for existing.
const TOTAL_STEPS = 4;

const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -20 },
};
const transition = { duration: 0.4, ease: [0.16, 1, 0.3, 1] };

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const { t } = useLanguage();

  const [step, setStep]                       = useState(1);
  const [sessionId, setSessionId]             = useState(null);
  const [companyAnalysis, setCompanyAnalysis] = useState(null);
  // Platform-selection step (Step2ChannelSelect) is intentionally SKIPPED — the
  // user asked to remove the platform question from onboarding. Channels are
  // configured later in the Settings page. Keep the state as an empty array
  // so downstream code (channel-settings API + StepBrandDNA prop) stays intact.
  const [selectedChannels] = useState([]);
  const [assetInspirations, setAssetInspirations] = useState([]);

  const isExistingUser = !!user;
  // Personen-Projekte bekommen nach der Brand DNA noch einen stillen Profil-Schritt (baut Ziel, Zielgruppe, Themen aus CV + Umfeld)
  const [personProjectId, setPersonProjectId] = useState(null);

  useEffect(() => {
    if (!user || !sessionStorage.getItem('project_onboarding')) return;
    axios.get(`${API}/projects/active`, { headers: getAuthHeader() })
      .then(({ data }) => { if (data.project?.project_type === 'person') setPersonProjectId(data.project.id); })
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    let id = localStorage.getItem('onboarding_session_id');
    if (!id) {
      id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('onboarding_session_id', id);
    }
    setSessionId(id);
  }, []);

  useEffect(() => {
    const cameFromProject = sessionStorage.getItem('project_onboarding');
    if (user && !cameFromProject && step === 1) {
      navigate('/app/kreation');
    }
  }, []); // eslint-disable-line

  // ── Handlers ────────────────────────────────────────────────────────────────

  const onStep1Complete = async (analysis) => {
    setCompanyAnalysis(analysis);
    // Logged-in users: attach the website analysis to the active project NOW,
    // so the Brand DNA step (and Settings) see the company data. Doing this
    // only at the very end meant step 5 generated a Brand DNA from nothing.
    if (isExistingUser) {
      try {
        await axios.post(
          `${API}/onboarding/claim-for-project`,
          { session_id: sessionId },
          { headers: getAuthHeader() }
        );
      } catch (err) {
        console.error('Claim after website analysis failed:', err);
      }
    }
    // Skip platform selection: new users → account creation (step 3);
    // existing users → asset upload (step 4).
    setStep(isExistingUser ? 4 : 3);
  };

  const onStep3Complete = () => setStep(4);
  const onStep4Complete = (data) => {
    if (data?.inspirations) setAssetInspirations(data.inspirations);
    setStep(5);
  };

  const onBrandDNAComplete = async () => {
    try {
      const channelsData = selectedChannels.map(platform => ({
        platform,
        frequency_per_week: 2,
        active: true,
      }));

      await axios.post(
        `${API}/onboarding/channel-settings`,
        { channels: channelsData },
        { headers: getAuthHeader() }
      );

      if (isExistingUser) {
        await axios.post(
          `${API}/onboarding/claim-for-project`,
          { session_id: sessionId },
          { headers: getAuthHeader() }
        ).catch(() => {});
      }
    } catch (err) {
      console.error('Channel settings save failed:', err);
    }

    if (personProjectId) { setStep(6); return; }
    finishOnboarding();
  };

  const finishOnboarding = () => {
    localStorage.removeItem('onboarding_session_id');
    sessionStorage.removeItem('project_onboarding');
    navigate('/app/kreation');
  };

  // ── Step indicator ──────────────────────────────────────────────────────────
  // Internal steps: 1=website, 3=account, 4=assets, 5=brand-dna (2 is skipped).
  // Map to visible progress: 1→1, 3→2, 4→3, 5→4 for new users; existing users
  // skip step 3 (account) as well, so 1→1, 4→2, 5→3.
  let displayStep = 1;
  if (isExistingUser) {
    displayStep = step === 1 ? 1 : step === 4 ? 2 : step === 5 ? 3 : step === 6 ? 4 : 1;
  } else {
    displayStep = step === 1 ? 1 : step === 3 ? 2 : step === 4 ? 3 : step === 5 ? 4 : 1;
  }
  const totalSteps  = (isExistingUser ? TOTAL_STEPS - 1 : TOTAL_STEPS) + (personProjectId ? 1 : 0);

  if (!sessionId) {
    return (
      <div className="min-h-screen bg-[#0A1628] flex items-center justify-center">
        <div className="animate-pulse text-[#5A6C7D]">{t.onboarding.loading}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A1628] flex flex-col" data-testid="onboarding-page">

      <header className="w-full px-6 py-4 border-b border-[#1A2B42]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <img
            src="https://customer-assets.emergentagent.com/job_kreation-hub/artifacts/haikxkku_xchannelsai_full_name_logo_transparent_colour.png"
            alt="xchannels.ai"
            className="h-12"
          />
          <div className="flex items-center gap-4">
            <LangToggle />
            {!isExistingUser && (
              <a href="/login" className="text-sm text-[#A0AEC0] hover:text-white transition-colors" data-testid="login-link">
                {t.onboarding.alreadyRegistered}
              </a>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        <div className="w-full max-w-xl">

          <StepIndicator currentStep={displayStep} totalSteps={totalSteps} />

          <AnimatePresence mode="wait">

            {step === 1 && (
              <motion.div key="s1" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <Step1WebsiteAnalysis sessionId={sessionId} onComplete={onStep1Complete} />
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="s3" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <Step3AccountCreation sessionId={sessionId} onComplete={onStep3Complete} onBack={() => setStep(1)} />
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="s4" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <StepAssetUpload onComplete={onStep4Complete} onBack={() => setStep(isExistingUser ? 1 : 3)} />
              </motion.div>
            )}

            {step === 5 && (
              <motion.div key="s5" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <StepBrandDNA
                  selectedChannels={selectedChannels}
                  inspirations={assetInspirations}
                  onComplete={onBrandDNAComplete}
                  onBack={() => setStep(4)}
                />
              </motion.div>
            )}

            {step === 6 && personProjectId && (
              <motion.div key="s6" variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={transition}>
                <StepPersonFinalize projectId={personProjectId} onComplete={finishOnboarding} />
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </main>
    </div>
  );
};

export default Onboarding;
